export interface PageTitleConfig {
  prefix?: string;
  suffix?: string;
  titles: Record<string, string>;
  dynamicPatterns: Array<{
    pattern: RegExp;
    generator: (match: RegExpMatchArray) => string;
  }>;
}

function capitalize(str: string): string {
  return str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Configuration for page titles based on URL paths.
 */
export const pageTitleConfig: PageTitleConfig = {
  prefix: 'PACE',
  suffix: '',

  // Static title mappings
  titles: {
    '/pace/approval-hub': 'Approval Hub',
    '/pace/execution-rules': 'Execution Rules',
    '/control-tower/roles': 'Roles',
    '/control-tower/auditable': 'Auditable',
    '/control-tower/pe-group-map': 'PE Group Map',
    '/control-tower/email-notifications': 'Email Notifications',
    '/control-tower/event-actions-log': 'Actions Log',
  },

  // Dynamic pattern matching
  dynamicPatterns: [
    // Plans pages
    {
      pattern: /^\/pace\/([^/]+)\/plans\/(scheduled|custom|approved|executed|historical)$/,
      generator: (match) => `${capitalize(match[2])} Plans`,
    },
    // Administration pages
    {
      pattern: /^\/pace\/([^/]+)\/administration\/([^/]+)$/,
      generator: (match) => {
        const adminPage = match[2];
        const titleMap: Record<string, string> = {
          state: 'State Administration',
          listprice: 'List Price',
          'comp-discount': 'Competitor Discount',
          'comp-ssoi': 'Competitor SSOI',
          'comp-distributor-markup': 'Competitor Distributor Markup',
          'comp-margin': 'Competitor Margin',
          'comp-pl-map': 'Default Competitor Marker',
          'comp-listprice': 'Competitor List Price',
          'comp-default-pl-map': 'Competitor Default PL Map',
          'comp-margin-to': 'Competitor Margin',
          'comp-mu': 'Competitor Markup',
          'comp-npp-published': 'Competitor NPP Published',
          'business-rules': 'Business Rules',
          'estimated-industry-volume': 'Estimated Industry Volume',
          'estimated-volume': 'Estimated Volume',
          'forecast-admin': 'Forecast Admin',
          'npp-export': 'NPP Export',
          'new-gross-margin': 'New Gross Margin',
          'gross-margin': 'Gross Margin',
          'state-product-attributes': 'Product Attributes',
          'take-rate-assumptions': 'Take Rate Assumptions',
          'vapor-estimated-industry-volume': 'Vapor Estimated Industry Volume',
          'vapor-estimated-volume': 'Vapor Estimated Volume',
          'published-npp': 'NPP',
          'vapor-sensitivity-estimated-volume': 'Vapor Sensitivity Estimated Volume',
          'vapor-gross-margin': 'Vapor Gross Margin',
          'vapor-markup': 'Vapor Markup',
          wpp: 'WPP',
          cos: 'Cost of Sales',
          markup: 'Markup',
          'product-sort': 'Product Sort',
          'program-groups': 'Program Groups',
          'rate-groups': 'Rate Groups',
          'rates-history': 'Rates History',
          'ssoi-history': 'SSOI History',
        };
        return titleMap[adminPage] || `${capitalize(adminPage)} Administration`;
      },
    },
    // Walk-forward pages
    {
      pattern: /^\/pace\/([^/]+)\/walk-forward\/([^/]+)$/,
      generator: (match) => `${capitalize(match[2])} Walk Forward`,
    },
    // NPI pages
    {
      pattern: /^\/pace\/([^/]+)\/npi\/(new-products-introduction|modified-product-lineups)$/,
      generator: (match) => {
        const npiType = match[2];
        return npiType === 'new-products-introduction' ? 'New Products Introduction' : 'Modified Product Lineups';
      },
    },
    // Scenario tabs
    {
      pattern:
        /^\/pace\/([^/]+)\/plans\/([^/]+)\/scenarios\/([^/]+)\/(comp_marker|inputs|mech_price|price_elasticity|results|market_volume|volume)$/,
      generator: (match) => {
        const tab = match[4];
        const tabTitles: Record<string, string> = {
          comp_marker: 'Competitive Marker',
          inputs: 'Inputs',
          mech_price: 'Mechanical Price',
          price_elasticity: 'Price Elasticity',
          results: 'Results',
          market_volume: 'Market Volume',
          volume: 'Volume',
        };
        return `Scenario - ${tabTitles[tab] || capitalize(tab)}`;
      },
    },
    // Walk-forward tabs
    {
      pattern: /^\/pace\/([^/]+)\/walk-forward\/([^/]+)\/([^/]+)\/(index|asp|results|lookup|str|inputs|volume)$/,
      generator: (match) => {
        const tab = match[4];
        const tabTitles: Record<string, string> = {
          index: 'Index',
          asp: 'ASP',
          results: 'Results',
          lookup: 'Lookup',
          str: 'STR',
          inputs: 'Inputs',
          volume: 'Volume',
        };
        return `Walk Forward - ${tabTitles[tab] || capitalize(tab)}`;
      },
    },
    // Annual Gantt
    {
      pattern: /^\/pace\/([^/]+)\/annual-gantt$/,
      generator: () => 'Annual Gantt Chart',
    },
    // Dev pages
    {
      pattern: /^\/pace\/([^/]+)\/dev\/([^/]+)$/,
      generator: (match) => `Dev - ${capitalize(match[2])}`,
    },
    // Admin pages
    {
      pattern: /^\/pace\/([^/]+)\/admin\/([^/]+)$/,
      generator: (match) => `Admin - ${capitalize(match[2])}`,
    },
    // Category-specific approval hub
    {
      pattern: /^\/pace\/([^/]+)\/approval-hub$/,
      generator: (match) => `${capitalize(match[1])} Approval Hub`,
    },
    // Category-specific execution rules
    {
      pattern: /^\/pace\/([^/]+)\/execution-rules$/,
      generator: (match) => `${capitalize(match[1])} Execution Rules`,
    },
    // Loyalty Offers
    {
      pattern: /^\/pace\/([^/]+)\/loyalty-offers$/,
      generator: () => 'Loyalty Offers',
    },
    {
      pattern: /^\/pace\/([^/]+)\/loyalty-offers\/([^/]+)(\/rates)?$/,
      generator: () => 'Loyalty Promotion Details',
    },
  ],
};

export function getPageTitle(pathname: string): string {
  const config = pageTitleConfig;

  // Check static titles first
  if (config.titles[pathname]) {
    return config.titles[pathname];
  }

  // Check dynamic patterns
  for (const { pattern, generator } of config.dynamicPatterns) {
    const match = pathname.match(pattern);
    if (match) {
      return generator(match);
    }
  }

  // Default fallback - try to generate from path
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0) {
    const lastSegment = segments[segments.length - 1];
    return capitalize(lastSegment);
  }

  return 'App';
}

export function formatDocumentTitle(pageTitle: string, prefix?: string, suffix?: string): string {
  const parts = [];

  if (prefix || pageTitleConfig.prefix) {
    parts.push(prefix || pageTitleConfig.prefix);
  }

  parts.push(pageTitle);

  if (suffix || pageTitleConfig.suffix) {
    parts.push(suffix || pageTitleConfig.suffix);
  }

  return parts.filter(Boolean).join(' | ');
}
